/**
 * Finance Leadership home — CFO single-pane-of-glass.
 * Consolidates Close Health, AR aging, cash-flow forecast, collector leaderboard,
 * and offers a board-ready PDF snapshot export.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PieChart as PieIcon, Download, RefreshCw, Sparkles, TrendingUp, AlertTriangle, Trophy, Lock } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const fmt = (n) => {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0)); }
  catch { return `₹${Number(n || 0).toFixed(2)}`; }
};

const BUCKET_COLOURS = {
  current: "bg-emerald-500", "1-30": "bg-yellow-500",
  "31-60": "bg-orange-500", "61-90": "bg-rose-500", "90+": "bg-rose-700",
};

export default function FinanceLeadership() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/finance/workspaces/${wsId}/leadership/snapshot`);
      setSnap(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const runAi = async () => {
    setAiBusy(true); setAi(null);
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/leadership/ai-summary`);
      setAi(data.ai);
    } catch (e) { toast.error(apiErrorMessage(e, "AI failed")); }
    finally { setAiBusy(false); }
  };

  const downloadBoardPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/finance/workspaces/${wsId}/leadership/board-report.pdf?include_ai=true`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `board-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success("Board report downloaded");
    } catch { toast.error("PDF download failed"); }
    finally { setPdfBusy(false); }
  };

  const health = snap?.close_health;
  const aging = snap?.ar_aging;
  const cashflow = snap?.cashflow_forecast;
  const leaderboard = snap?.collector_leaderboard || [];
  const buckets = aging?.buckets || {};
  const totalAging = aging?.total || 0;
  const score = health?.last_month_readiness;
  const tone = score == null ? "text-muted-foreground" : score >= 80 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-rose-600";

  return (
    <div className="space-y-6" data-testid="finance-leadership-page">
      <PageHeader
        title="Finance Leadership"
        subtitle="Single pane of glass — close health, AR aging, cash-flow forecast, and collector performance."
        icon={PieIcon}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={load} disabled={loading} data-testid="lead-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Refresh
        </Button>
        <Button onClick={runAi} disabled={aiBusy || !snap} data-testid="lead-ai-btn">
          {aiBusy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {aiBusy ? "Generating…" : "AI executive summary"}
        </Button>
        <Button onClick={downloadBoardPdf} disabled={pdfBusy || !snap} variant="secondary" data-testid="lead-board-pdf">
          {pdfBusy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          {pdfBusy ? "Preparing…" : "Board-ready PDF"}
        </Button>
      </div>

      {ai && (
        <Card className="border-primary/30 bg-primary/[0.03]"><CardContent className="p-5 space-y-3" data-testid="lead-ai-card">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <div className="font-semibold text-lg" data-testid="lead-ai-headline">{ai.headline}</div>
          </div>
          <div className="text-sm whitespace-pre-wrap" data-testid="lead-ai-narrative">{ai.narrative}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ai.key_risks?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase text-rose-600 tracking-wide mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />Key risks
                </div>
                <ul className="text-sm space-y-1">
                  {ai.key_risks.map((r, i) => (
                    <li key={i} className="border-l-2 border-rose-500/40 pl-2">
                      <b>{r.label}</b> <Badge variant="outline" className="text-[9px] ml-1">{r.severity}</Badge>
                      <div className="text-xs text-muted-foreground">{r.detail}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ai.key_opportunities?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase text-emerald-600 tracking-wide mb-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />Opportunities
                </div>
                <ul className="text-sm space-y-1">
                  {ai.key_opportunities.map((o, i) => (
                    <li key={i} className="border-l-2 border-emerald-500/40 pl-2">
                      <b>{o.label}</b>
                      <div className="text-xs text-muted-foreground">{o.detail}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {ai.board_talking_points?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide mb-1">Board talking points</div>
              <ul className="text-sm list-disc pl-5 space-y-0.5">
                {ai.board_talking_points.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}
        </CardContent></Card>
      )}

      {/* KPI ribbon */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            <Lock className="w-3 h-3" />Close Health
          </div>
          <div className={`text-3xl font-mono mt-1 ${tone}`} data-testid="lead-close-score">
            {score == null ? "—" : `${score}%`}
          </div>
          <div className="text-xs text-muted-foreground">{health?.last_month} · last run {health?.days_since_last_run != null ? `${health.days_since_last_run}d ago` : "never"}</div>
        </CardContent></Card>

        <Card><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">AR outstanding</div>
          <div className="text-3xl font-mono mt-1 text-rose-600" data-testid="lead-ar-total">{fmt(totalAging)}</div>
          <div className="text-xs text-muted-foreground">
            {buckets["90+"]?.count ? `${buckets["90+"].count} invoices > 90 days overdue` : "no >90d invoices"}
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Cash-flow (12w net)</div>
          <div className={`text-3xl font-mono mt-1 ${(cashflow?.total_net_inflow || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="lead-cf-net">
            {fmt(cashflow?.total_net_inflow || 0)}
          </div>
          <div className="text-xs text-muted-foreground">Ending: {fmt(cashflow?.ending_cash || 0)}</div>
        </CardContent></Card>

        <Card><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            <Trophy className="w-3 h-3" />Recovered (30d)
          </div>
          <div className="text-3xl font-mono mt-1 text-emerald-600" data-testid="lead-recovered">
            {fmt(snap?.collector_totals?.amount_recovered || 0)}
          </div>
          <div className="text-xs text-muted-foreground">{snap?.collector_totals?.reminders_sent || 0} reminders · {snap?.collector_totals?.customers_worked || 0} customers</div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* AR aging bar */}
        <Card><CardContent className="p-4 space-y-2" data-testid="lead-ar-panel">
          <div className="text-sm font-semibold">AR aging by bucket</div>
          {["current", "1-30", "31-60", "61-90", "90+"].map((k) => {
            const b = buckets[k] || { amount: 0, count: 0 };
            const pct = totalAging ? Math.round((b.amount / totalAging) * 100) : 0;
            return (
              <div key={k} className="space-y-0.5" data-testid={`lead-bucket-${k}`}>
                <div className="flex justify-between text-xs">
                  <span className="font-medium">{k === "current" ? "Current" : `${k} days overdue`}</span>
                  <span className="font-mono">{fmt(b.amount)} · {b.count} inv</span>
                </div>
                <div className="w-full h-2 bg-muted rounded overflow-hidden">
                  <div className={`h-full ${BUCKET_COLOURS[k]}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </CardContent></Card>

        {/* Cash-flow sparkline */}
        <Card><CardContent className="p-4 space-y-2" data-testid="lead-cf-panel">
          <div className="text-sm font-semibold">Cash-flow forecast — next 12 weeks</div>
          {cashflow?.weeks?.length ? (
            <div className="space-y-1">
              {cashflow.weeks.slice(0, 12).map((w, i) => {
                const net = Number(w.net || 0);
                const maxAbs = Math.max(1, ...cashflow.weeks.map((wk) => Math.abs(Number(wk.net || 0))));
                const pct = Math.max(2, Math.min(98, Math.round(50 + (net / maxAbs) * 45)));
                return (
                  <div key={i} className="flex items-center gap-2 text-xs" data-testid={`lead-cf-week-${i}`}>
                    <div className="w-14 text-muted-foreground shrink-0">{(w.week_start || "").slice(5)}</div>
                    <div className="flex-1 relative h-4 bg-muted rounded">
                      <div className={`absolute top-0 bottom-0 ${net >= 0 ? "bg-emerald-500/60 left-1/2" : "bg-rose-500/60 right-1/2"}`}
                        style={net >= 0 ? { width: `${pct - 50}%` } : { width: `${50 - pct}%` }}
                      />
                      <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
                    </div>
                    <div className={`w-24 text-right font-mono ${net >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmt(net)}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-4">No forecast data available.</div>
          )}
        </CardContent></Card>
      </div>

      <Card><CardContent className="p-0"><div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="lead-leaderboard">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Collector (30-day leaderboard)</th>
              <th className="text-right px-4 py-3">Reminders</th>
              <th className="text-right px-4 py-3">Recovered</th>
              <th className="text-right px-4 py-3">Recovery %</th>
              <th className="text-right px-4 py-3">Promise-keep %</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No collections activity yet.</td></tr>
            )}
            {leaderboard.map((r, i) => (
              <tr key={r.user_id} className="border-t" data-testid={`lead-lb-${r.user_id}`}>
                <td className="px-4 py-3">
                  {i === 0 && <Trophy className="w-4 h-4 inline mr-1 text-amber-500" />}
                  <b>{r.user_name}</b>
                </td>
                <td className="px-4 py-3 text-right">{r.reminders_sent}</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-600">{fmt(r.amount_recovered)}</td>
                <td className="px-4 py-3 text-right">{r.recovery_rate != null ? `${r.recovery_rate}%` : "—"}</td>
                <td className="px-4 py-3 text-right">{r.promise_keep_rate != null ? `${r.promise_keep_rate}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div></CardContent></Card>
    </div>
  );
}
